import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Loader2, Plus, Trash2, Edit2, Search, Database, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  telemetry_providers: 'Telemetry Providers',
  payment_systems: 'Payment Systems (Readers, Coin, Bill, Mobile)',
  payment_system_types: 'Payment System Types',
  machine_manufacturers: 'Machine Manufacturers',
  machine_models: 'Machine Models',
  machine_types: 'Machine Types',
  currencies: 'Currencies (ISO-4217)',
  coin_denominations: 'Coin Denominations',
  bill_denominations: 'Bill Denominations',
  dex_field_mappings: 'DEX / EVA-DTS Field Mappings',
  connectivity_types: 'Connectivity Types',
  refrigeration_types: 'Refrigeration Types',
};

// v2.5.878 — Platform Admin CRUD for universal reference data.
// One page controls every dropdown across every module — a standing-order
// pattern extensible to any future reference list.
export default function ReferenceDataPage() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('telemetry_providers');
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {code, name, metadata_json, sort_order, is_active}
  const [showDialog, setShowDialog] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/reference-data/categories`);
      setCategories(data.categories || []);
    } catch (e) {
      toast.error('Could not load categories');
    }
  }, []);

  const loadItems = useCallback(async () => {
    if (!selectedCategory) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/reference-data/${selectedCategory}`, {
        params: { include_inactive: true, q: q || undefined },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load items');
    } finally { setLoading(false); }
  }, [selectedCategory, q]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const openCreate = () => {
    setEditing({ code: '', name: '', parent_code: '', metadata_json: '{}', sort_order: 100, is_active: true, _isNew: true });
    setShowDialog(true);
  };
  const openEdit = (item) => {
    setEditing({
      ...item,
      metadata_json: JSON.stringify(item.metadata || {}, null, 2),
      _isNew: false,
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!editing.code.trim() || !editing.name.trim()) {
      toast.error('Code and Name are required');
      return;
    }
    let metadata;
    try { metadata = JSON.parse(editing.metadata_json || '{}'); }
    catch { toast.error('Metadata must be valid JSON'); return; }

    try {
      if (editing._isNew) {
        await axios.post(`${API}/api/reference-data`, {
          category: selectedCategory,
          code: editing.code.trim(),
          name: editing.name.trim(),
          parent_code: editing.parent_code?.trim() || null,
          metadata,
          sort_order: editing.sort_order,
          is_active: editing.is_active,
        });
        toast.success(`Added ${editing.name}`);
      } else {
        await axios.put(`${API}/api/reference-data/${selectedCategory}/${editing.code}`, {
          name: editing.name.trim(),
          parent_code: editing.parent_code?.trim() || null,
          metadata,
          sort_order: editing.sort_order,
          is_active: editing.is_active,
        });
        toast.success(`Updated ${editing.name}`);
      }
      setShowDialog(false);
      loadItems();
      loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (item) => {
    const label = item.is_system ? 'deactivate' : 'delete';
    if (!window.confirm(`${label.toUpperCase()} "${item.name}"?${item.is_system ? '\n\nThis is a system entry — it will be deactivated, not removed.' : ''}`)) return;
    try {
      const { data } = await axios.delete(`${API}/api/reference-data/${selectedCategory}/${item.code}`);
      toast.success(`${data.action === 'deactivated' ? 'Deactivated' : 'Deleted'}: ${item.name}`);
      loadItems(); loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const reseed = async () => {
    if (!window.confirm('Force re-seed all system reference data? Custom operator entries will be preserved.')) return;
    try {
      const { data } = await axios.post(`${API}/api/reference-data/reseed`);
      toast.success(`Reseeded — ${data.stats.upserted} entries updated across ${data.stats.categories} categories`);
      loadItems(); loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reseed failed');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="reference-data-blocked">
        <Card className="border-red-200 bg-red-50"><CardContent className="p-6">
          <div className="font-semibold text-red-900">Platform Admin only</div>
          <div className="text-sm text-red-800 mt-1">Reference data management requires Platform Owner access.</div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="reference-data-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Database className="h-5 w-5 sm:h-6 sm:w-6 text-sky-600" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" data-testid="reference-data-title">Reference Data</h1>
            <Badge className="bg-sky-100 text-sky-800 border-0 text-[10px]">Platform Admin</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-medium">Universal dropdowns — telemetry providers, payment systems, machine manufacturers, currencies, DEX field mappings. One source of truth for every module.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={reseed} className="h-9 text-xs" data-testid="reseed-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reseed system data
          </Button>
          <Button size="sm" onClick={openCreate} className="h-9 text-xs bg-sky-600 hover:bg-sky-700" data-testid="add-item-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="h-9 text-sm sm:max-w-md" data-testid="category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {categories.map(c => (
              <SelectItem key={c.category} value={c.category}>
                {CATEGORY_LABELS[c.category] || c.category} ({c.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-medium" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or code…" className="pl-9 h-9 text-sm" data-testid="ref-search" />
        </div>
      </div>

      {loading && <div className="text-sm text-gray-medium py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

      {!loading && (
        <Card><CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs sm:text-sm min-w-[720px]" data-testid="ref-data-table">
            <thead className="bg-gray-50 border-b text-gray-medium">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Code</th>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Parent</th>
                <th className="text-left px-3 py-2 font-medium">Metadata</th>
                <th className="text-center px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.code} className="border-b last:border-0 hover:bg-gray-50" data-testid={`ref-row-${item.code}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{item.code}</td>
                  <td className="px-3 py-2 font-medium">{item.name}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-medium">{item.parent_code || '—'}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-gray-medium max-w-[280px] truncate">{JSON.stringify(item.metadata || {})}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex gap-1 justify-center">
                      {item.is_active
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Active</Badge>
                        : <Badge className="bg-gray-200 text-gray-700 border-0 text-[10px]">Inactive</Badge>}
                      {item.is_system && <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px]">System</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="h-7 w-7 p-0" data-testid={`edit-${item.code}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(item)} className="h-7 w-7 p-0 text-red-600" data-testid={`delete-${item.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-medium">No items in this category. Click <b>Add Item</b> to create one.</td></tr>}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?._isNew ? 'Add' : 'Edit'} {CATEGORY_LABELS[selectedCategory] || selectedCategory} Entry</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Code (short unique identifier)</Label>
                <Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} disabled={!editing._isNew} placeholder="e.g. nayax, USD, mei_cashflow_7900" data-testid="dialog-code" />
              </div>
              <div>
                <Label className="text-xs">Name (display label)</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nayax VPOS Touch" data-testid="dialog-name" />
              </div>
              <div>
                <Label className="text-xs">Parent Code (optional, for hierarchies)</Label>
                <Input value={editing.parent_code || ''} onChange={(e) => setEditing({ ...editing, parent_code: e.target.value })} placeholder="e.g. cashless_reader (for payment_systems)" data-testid="dialog-parent" />
              </div>
              <div>
                <Label className="text-xs">Metadata (JSON)</Label>
                <textarea
                  value={editing.metadata_json}
                  onChange={(e) => setEditing({ ...editing, metadata_json: e.target.value })}
                  className="w-full text-xs font-mono border rounded p-2 min-h-[100px]"
                  placeholder='{"country": "US", "decimal_places": 2}'
                  data-testid="dialog-metadata"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Sort Order</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 100 })} data-testid="dialog-sort" />
                </div>
                <div className="flex items-end gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="dialog-active" />
                    Active
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-sky-600 hover:bg-sky-700" data-testid="dialog-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
